import { Visibility } from '@mui/icons-material'
import {
  Button,
  Typography,
  Box,
  Tooltip,
} from '@mui/material'
import {
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid'
import { useNavigate } from "react-router-dom"

import { type AIQCReview } from "../../api"
import { useUrlPaginationModel } from "../../hooks/use-url-pagination-model"
import { AdminDataGrid } from "../admin-data-grid"
import { DateCell, LinkCell, StatusChip } from "../data-grid-cells"
import { truncateId } from "../data-grid-utils"

type ReviewedTableProps = {
  reviews: AIQCReview[]
}

export function ReviewedTable({ reviews }: ReviewedTableProps) {
  const navigate = useNavigate()
  const { paginationModel, onPaginationModelChange } = useUrlPaginationModel()

  const columns: GridColDef<AIQCReview>[] = [
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => row.session?.user?.email,
      renderCell: (params: GridRenderCellParams<AIQCReview>) => {
        const user = params.row.session?.user
        if (!user) {
          return <Typography variant="body2" color="text.secondary">-</Typography>
        }
        return (
          <Box sx={{ py: 0.5 }}>
            <LinkCell to={`/users/${user.id}`}>
              {user.email}
            </LinkCell>
            {user.firstName && user.lastName && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {user.firstName} {user.lastName}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'orderId',
      headerName: 'Order ID',
      flex: 0.7,
      minWidth: 150,
      valueGetter: (_, row) => {
        const metadata = row.session?.metadata
        return metadata?.junctionLabOrderId || metadata?.labOrderId
      },
      renderCell: (params: GridRenderCellParams<AIQCReview>) => {
        const junctionOrderId = params.row.session?.metadata?.junctionLabOrderId
        const labOrderId = params.row.session?.metadata?.labOrderId
        const displayId = junctionOrderId || labOrderId

        if (!displayId) {
          return <Typography variant="body2" color="text.secondary">-</Typography>
        }

        const linkTarget = labOrderId || junctionOrderId
        return (
          <LinkCell to={`/orders/${linkTarget}`} monospace>
            {truncateId(displayId)}
          </LinkCell>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams<AIQCReview>) => {
        const status = params.row.status
        const getStatusColor = () => {
          switch (status?.toLowerCase()) {
            case 'approved': return 'success' as const
            case 'rejected': return 'error' as const
            default: return 'default' as const
          }
        }

        return (
          <StatusChip
            label={status?.toUpperCase() || 'UNKNOWN'}
            color={getStatusColor()}
          />
        )
      },
    },
    {
      field: 'reviewedBy',
      headerName: 'Reviewed By',
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.reviewedBy?.email,
      renderCell: (params: GridRenderCellParams<AIQCReview>) => {
        const reviewer = params.row.reviewedBy
        if (!reviewer) {
          return <Typography variant="body2" color="text.secondary">-</Typography>
        }
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {reviewer.firstName} {reviewer.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {reviewer.email}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Reviewed Date',
      flex: 0.8,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<AIQCReview>) => (
        <DateCell value={params.row.updatedAt} />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AIQCReview>) => {
        const review = params.row
        const reviewedDate = new Date(review.updatedAt).toLocaleDateString()
        const reviewerName = review.reviewedBy
          ? `${review.reviewedBy.firstName} ${review.reviewedBy.lastName}`
          : 'Unknown reviewer'

        const tooltipText = `Review ${review.status?.toUpperCase() || 'UNKNOWN'} by ${reviewerName} on ${reviewedDate}`

        return (
          <Tooltip title={tooltipText} placement="left">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Visibility />}
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/ai-qc/review/${params.row.id}`)
              }}
            >
              View
            </Button>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <AdminDataGrid
      rows={reviews}
      columns={columns}
      rowHeight={80}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={(params) => navigate(`/ai-qc/review/${params.id}`)}
    />
  )
}
